import { Component, DestroyRef, OnDestroy, computed, effect, inject, input, output, signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import {
  IAdminAndUserAuthResponse,
  IAdminAndUserAuthsReq,
  IAdminClients,
  ICreateManageUserState,
  IManageuser,
  IManageUserAuthsReq,
  initialCreateManageUserState
} from 'src/app/store/createManageUser/state';
import { Lux } from '@roosevelt/common-ui-lib/core';
import {
  createAllGroupsAuthorizationForm,
  isDelegateUser,
  refreshDirtyStateFromSnapshot,
  syncNestedUserAuthControl
} from '../manage-auth-form.util';
import { ManageAuthSharedBase } from '../manage-auth-shared.base';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'bmt-manage-auth-at-group',
    templateUrl: './at-group-level.component.html',
    styleUrls: ['./at-group-level.component.scss'],
    standalone: false
})
export class ManageAuthAtGroupComponent extends ManageAuthSharedBase implements OnDestroy {
  readonly userInfo = input.required<IManageuser>();
  readonly cancelGroupAuthEdit = output<boolean>();
  readonly viewAuthorizations = output<boolean>();
  private readonly destroyRef = inject(DestroyRef);


  private readonly authsModifiedForAllSubGroupsState = signal(false);
  private readonly formChangesDetecedForCustomEditState = signal(false);
  private readonly displayConvertToUserCheckState = signal(false);
  private readonly isSingleClientAdminState = computed(() => ['BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN'].includes(this.userInfo().adminRole));
  private readonly totalAuthsState = computed(() => this.userAndAdminAuthsState()?.pagination?.totalRecords || 0);
  private readonly adminSubGroupAuthsState = computed<Record<string, any>>(() => {
    const selectedOption = this.selectedOptionState();
    const adminAndUserAuths = this.userAndAdminAuthsState();
    const userRelatedAuthorizations = adminAndUserAuths.userRelatedAuthorizations;
    const adminRole = this.userInfo().adminRole;

    if (selectedOption !== 'customizeClientSubgroups' || !userRelatedAuthorizations.some(auths => auths.subGroupSpecifiedIdentifier)) {
      return {};
    }

    const objectToConsider = !this.singleClientOrSubGroupAdmins.includes(adminRole)
      ? adminAndUserAuths.adminRelatedAuthorizations.subGroupAuthorizations || []
      : userRelatedAuthorizations;

    return this.createAdminSubGrpAuthsObj(objectToConsider);
  });

  defaultPageSize = 100;
  selectedIndex = 0;
  sortField: string = '';
  sortOrder: string = 'ASC';
  editAuthorizations = false;
  adminClientsInfo: IAdminClients[];
  initialSnapshotAllSubGroups: string;
  editAllSubGroupsForm: UntypedFormGroup;

  inquiryOptions = [{
    label: 'Inquiry',
    value: 'Inquiry'
  }, {
    label: 'None',
    value: 'None'
  }];

  noneOptions = [{
    label: 'None',
    value: 'None'
  }];

  updateOptions = [{
    label: 'Update',
    value: 'Update'
  }, {
    label: 'Inquiry',
    value: 'Inquiry'
  }, {
    label: 'None',
    value: 'None'
  }];


  forNewlyCreatedUser = [{
    eligibilityAccessLevel: '',
    clientKnowledgeIndicator: false,
    billingReportAccessIndicator: false,
    customClientReportAccessIndicator: false,
    claimDetailReportAccessIndicator: false,
    satAccessLevel: '',
    satAccessReportIndicator: false
  }];
  snapshotAllOfCustomSubGroupForms: string;
  singleClientOrSubGroupAdmins = ['BMT-CLIENT-ASSOCIATION-ADMIN', 'BMT-CLIENT-ASSOCIATION-DELEGATE', 'BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN', 'BMT-GROUP-DELEGATE', 'BMT-SUBGROUP-DELEGATE'];

  constructor(private readonly fb: UntypedFormBuilder,
            private readonly router: Router,
            lux: Lux<{ createManageUserState: ICreateManageUserState }>,
            private readonly createManageUserAsync: CreateManageUserAsync) {
    super(lux, 'BMT-edit-manage-auth-at-group' + ManageAuthAtGroupComponent.name + new Date().toISOString());
    effect(() => {
      this.displayConvertToUserCheckState.set(this.isSingleClientAdminState() && isDelegateUser(this.userInfo().selectedUserType));
    });

    effect(() => {
      const selectedOption = this.selectedOptionState();
      const adminAndUserAuths = this.userAndAdminAuthsState();

      this.editAllSubGroupsForm = null;
      this.authsModifiedForAllSubGroupsState.set(false);
      this.formChangesDetecedForCustomEditState.set(false);

      if (!selectedOption || selectedOption === 'revokeAtClient' || selectedOption === 'delegate' || !adminAndUserAuths.userRelatedAuthorizations.length) {
        return;
      }

      if (selectedOption === 'clientAllSubgroups') {
        this.createEditAllSubGorupsForm();
        return;
      }

      if (selectedOption === 'customizeClientSubgroups' && adminAndUserAuths.userRelatedAuthorizations.filter(auths => auths.subGroupSpecifiedIdentifier).length) {
        this.createCustomAuthsForSubgroups();
      }
    });
  }

  get updatingAuths(): boolean {
    return this.updatingAuthInProgressState();
  }

  get totalAuths(): number {
    return this.totalAuthsState();
  }

  get authsModifiedForAllSubGroups(): boolean {
    return this.authsModifiedForAllSubGroupsState();
  }

  get formChangesDetecedForCustomEdit(): boolean {
    return this.formChangesDetecedForCustomEditState();
  }

  get displayConvertToUserCheck(): boolean {
    return this.displayConvertToUserCheckState();
  }

  set displayConvertToUserCheck(value: boolean) {
    this.displayConvertToUserCheckState.set(value);
  }

  get adminSubGroupAuths(): Record<string, any> {
    return this.adminSubGroupAuthsState();
  }

  get isASingleClientAdmin(): boolean {
    return this.isSingleClientAdminState();
  }

  protected get fallbackUserAuthorizations() {
    return this.forNewlyCreatedUser;
  }

  protected get initialAdminUserAuthResponse(): IAdminAndUserAuthResponse {
    return initialCreateManageUserState.adminAndUserAuths.authResponse;
  }

  protected get managedUserInfo(): IManageuser {
    return this.userInfo();
  }

  protected onManageUserAuthSuccess(selectedOption: string): void {
    this.formChangesDetecedForCustomEditState.set(false);
    this.authsModifiedForAllSubGroupsState.set(false);
    this.displayConvertToUserCheckState.set(this.isSingleClientAdminState() && selectedOption === 'delegate');
  }

  ngOnDestroy(): void {
    this.destroyManageUserState();
  }

  fetchUserAuthorizations(offset: number): void {
    const userInfo = this.userInfo();
    const userContextStr = userInfo.contextOfAdmin.split('/');
    const adminAndUserAuthReq: IAdminAndUserAuthsReq = {
      userIdentifier: userInfo.selectedUserId,
      payer: userContextStr[1],
      payerAdmin: userContextStr[0],
      groupAccessLevel: (this.selectedOption === 'clientAllSubgroups') ? 'all' : 'one',
      groupSpecifiedIdentifier: userContextStr[2],
      ...userContextStr[3] && { subGroupSpecifiedIdentifier: userContextStr[3] },
      pagination: {
        offset: offset > this.totalAuths ? (offset - this.totalAuths) : offset,
        limit: this.defaultPageSize
      },
      sort: [
        {
          fieldName: 'specifiedId',
          order: 'ASC'
        }
      ]
    };
    this.lux.set(state => state.createManageUserState.adminAndUserAuths, { authRequest: adminAndUserAuthReq });
    this.createManageUserAsync.getAdminAndUserAuths('edit')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getMoreAuths(page) {
    this.fetchUserAuthorizations(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }

  manageUserAuthFor(option) {
    this.clearManageUserAuthFeedback();
    this.selectedOptionState.set(option);
    if (option !== 'revokeAtClient' && option !== 'delegate') {
      this.fetchUserAuthorizations(0);
    }
  }

  manageAuthorizations() {
    const userInfo = this.userInfo();
    const userContextStr = userInfo.contextOfAdmin.split('/');
    const manageUserReq: IManageUserAuthsReq = {
      userIdentifier: userInfo.selectedUserId,
      userType: this.selectedOption !== 'delegate' ? 'user' : 'delegate'
    };
    if (this.selectedOption !== 'delegate') {
      const userAuthForm = this.editAllSubGroupsForm?.controls?.userAuthForm;
      const allSubGroupAuthValues = (this.selectedOption === 'clientAllSubgroups')
        ? (userAuthForm as UntypedFormGroup | undefined)?.getRawValue()
        : null;
      const customSubGroupAuthValues = (this.selectedOption === 'customizeClientSubgroups')
        ? (userAuthForm as UntypedFormArray | undefined)?.getRawValue()
        : [];
      const dataToCrossCheck = this.selectedOption === 'customizeClientSubgroups' ? JSON.parse(this.snapshotAllOfCustomSubGroupForms) : {};

      manageUserReq.authorizations = [
        {
          payers: [userContextStr[1]],
          groupAccessLevel: 'one',
          groupSpecifiedIdentifier: userContextStr[2],
          ...(this.selectedOption === 'revokeAtClient') && { subgroupAccessLevel: 'none' },
          ...(this.selectedOption === 'clientAllSubgroups') && {
            subgroupAccessLevel: 'all',
            subgroupAuthorizations: [
              {
                eligibilityAccessLevel: allSubGroupAuthValues?.userEligAccess,
                satAccessLevel: allSubGroupAuthValues?.userSatAccess,
                clientKnowledgeIndicator: allSubGroupAuthValues?.userClientKnowledge,
                customClientReportAccessIndicator: allSubGroupAuthValues?.userCustomClientReportAccess,
                billingReportAccessIndicator: allSubGroupAuthValues?.userBillingReport,
                claimDetailReportAccessIndicator: allSubGroupAuthValues?.userClientDetailReport
              }
            ]
          },
          ...(this.selectedOption === 'customizeClientSubgroups') && {
            subgroupAccessLevel: 'custom',
            subgroupAuthorizations: customSubGroupAuthValues.map((subGrpFrmVal, index) => {
              const authObj = {
                subgroupSpecifiedIdentifier: subGrpFrmVal.id,
                eligibilityAccessLevel: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userEligAccess : 'None',
                satAccessLevel: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userSatAccess : 'None',
                customClientReportAccessIndicator: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userCustomClientReportAccess : false,
                clientKnowledgeIndicator: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userClientKnowledge : false,
                billingReportAccessIndicator: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userBillingReport : false,
                claimDetailReportAccessIndicator: subGrpFrmVal.userPrivilege ? subGrpFrmVal.userClientDetailReport : false
              };
              // Changes to submit are only the changes on the selected subgroup or if the subgroup in deselected (added a condition to collect changes when converting to user)
              const isDelegate = isDelegateUser(userInfo.selectedUserType);
              const hasPrivilegeChanged = dataToCrossCheck[index].userPrivilege !== subGrpFrmVal.userPrivilege;
              const hasAnyChange = JSON.stringify(dataToCrossCheck[index]) !== JSON.stringify(subGrpFrmVal);
              const shouldIncludeChanges = isDelegate || ((hasPrivilegeChanged || subGrpFrmVal.userPrivilege) && hasAnyChange);
              return shouldIncludeChanges ? authObj : null;
            }).filter(val => val !== null)
          }
        }
      ];
    }
    this.triggerManageUserAuth(manageUserReq);
  }

  triggerManageUserAuth(manageUserReq: IManageUserAuthsReq): void {
    this.prepareManageUserAuthSubmission();
    this.lux.set(state => state.createManageUserState.manageUserAuths, { authRequest: manageUserReq });
    this.createManageUserAsync.manageUserAuthorizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.completeManageUserAuthSubmission();
      });
  }

  navigateToUserAdmin(action) {
    if (action === 'viewAuth') {
      this.clearManageUserAuthFeedback();
      this.viewAuthorizations.emit(true);
    }
    if (action === 'close') {
      this.clearManageUserAuthFeedback();
      this.cancelGroupAuthEdit.emit(true);
    }
    if (action === 'manage') {
      this.clearManageUserAuthFeedback();
      this.router.navigate(['userAdmin/manage']);
    }
  }

  createEditAllSubGorupsForm() {
    const { editForm, initialSnapshot } = createAllGroupsAuthorizationForm(
      this.fb,
      this.userAndAdminAuths.adminRelatedAuthorizations,
      this.userAndAdminAuths.userRelatedAuthorizations[0]
    );
    this.editAllSubGroupsForm = editForm;
    this.initialSnapshotAllSubGroups = initialSnapshot;
    this.authsModifiedForAllSubGroupsState.set(false);
  }

  createCustomAuthsForSubgroups() {
    const userAuths = this.userAndAdminAuths.userRelatedAuthorizations;
    const adminSubGroupAuths = this.adminSubGroupAuths;

    if (Object.keys(adminSubGroupAuths).length) {
      this.editAllSubGroupsForm = this.fb.group({
        selectAllSubGroups: false,
        adminEligAccess: '',
        adminClientKnowledge: false,
        adminCustomClientReport: false,
        adminBillingReport: false,
        adminClientDetailReport: false,
        adminSatAccess: '',
        userAuthForm: userAuths.length ? new UntypedFormArray(userAuths.map(subgroup => {
          return this.fb.group({
            id: subgroup.subGroupSpecifiedIdentifier,
            userPrivilege: subgroup.userPrivilegeSelected,
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].eligibilityAccessLevel && { userEligAccess: subgroup.eligibilityAccessLevel || 'None' },
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].customClientReportAccessIndicator && { userCustomClientReportAccess: subgroup.customClientReportAccessIndicator || false },
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].clientKnowledgeIndicator && { userClientKnowledge: subgroup.clientKnowledgeIndicator || false },
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].billingReportAccessIndicator && { userBillingReport: subgroup.billingReportAccessIndicator || false },
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].claimDetailReportAccessIndicator && { userClientDetailReport: subgroup.claimDetailReportAccessIndicator || false },
            ...adminSubGroupAuths[subgroup.subGroupSpecifiedIdentifier].satAccessLevel && { userSatAccess: subgroup.satAccessLevel || 'None' }
          });
        })) : '',
      });
      const editFromContrls = this.editAllSubGroupsForm.controls;

      const userAuthForm = editFromContrls.userAuthForm as UntypedFormArray;
      this.snapshotAllOfCustomSubGroupForms = JSON.stringify(userAuthForm.getRawValue());
      this.formChangesDetecedForCustomEditState.set(false);
    }
  }
  createAdminSubGrpAuthsObj(authsToConsider) {
    return authsToConsider.length ? authsToConsider.reduce((bag, key) => ({
      ...bag,
      [key.subGroupSpecifiedIdentifier]: !this.singleClientOrSubGroupAdmins.includes(this.userInfo().adminRole)
        ? key
        : this.userAndAdminAuths.adminRelatedAuthorizations
    }), {}) : {};
  }

  syncSelectAllSubGroups(): void {
    const userAuthForm = this.editAllSubGroupsForm?.controls?.userAuthForm as UntypedFormArray;
    const shouldSelect = !!this.editAllSubGroupsForm?.controls?.selectAllSubGroups?.value;
    userAuthForm?.controls?.forEach((form: UntypedFormGroup) => {
      form.controls.userPrivilege?.setValue(shouldSelect);
    });
    this.refreshCustomSubGroupDirtyState();
  }

  syncAdminValue(adminControlName: string, userControlName: string, requireTruthy = false): void {
    if (this.selectedOption === 'clientAllSubgroups') {
      syncNestedUserAuthControl(
        this.editAllSubGroupsForm,
        adminControlName,
        userControlName,
        () => this.refreshAllSubGroupDirtyState(),
        requireTruthy
      );
      return;
    }

    const adminValue = this.editAllSubGroupsForm?.controls?.[adminControlName]?.value;
    if (requireTruthy && !adminValue) {
      return;
    }

    const userAuthForm = this.editAllSubGroupsForm?.controls?.userAuthForm;
    if (!userAuthForm) {
      return;
    }

    (userAuthForm as UntypedFormArray).controls?.forEach((form: UntypedFormGroup) => {
      form.controls?.[userControlName]?.setValue(adminValue);
    });
    this.refreshCustomSubGroupDirtyState();
  }

  refreshAllSubGroupDirtyState(): void {
    refreshDirtyStateFromSnapshot(
      this.editAllSubGroupsForm?.controls?.userAuthForm as UntypedFormGroup | undefined,
      value => this.authsModifiedForAllSubGroupsState.set(value),
      this.initialSnapshotAllSubGroups,
      isDelegateUser(this.userInfo().selectedUserType)
    );
  }

  refreshCustomSubGroupDirtyState(): void {
    const userAuthForm = this.editAllSubGroupsForm?.controls?.userAuthForm as UntypedFormArray | undefined;
    if (!userAuthForm) {
      this.formChangesDetecedForCustomEditState.set(false);
      return;
    }

    this.formChangesDetecedForCustomEditState.set(
      isDelegateUser(this.userInfo().selectedUserType)
        || (this.snapshotAllOfCustomSubGroupForms !== JSON.stringify(userAuthForm.getRawValue()))
    );
  }

}
